<template>
  <Modal
    :name="$constants.dynamicModals.SIGN_UP_MODAL"
    custom
    :auto-focusable="!withAdditionalQuestion"
    :persistent="$route.path.includes('login-external')"
  >
    <template #default="{ persistent, onClose }">
      <ModalBlack
        class="war-room-sign-up-modal"
        :class="{ 'war-room-sign-up-modal_extended-height': isUserInsideExternalApp }"
        :persistent="persistent"
        @close="onClose"
      >
        <template #header>
          <FadeTransition>
            <button v-if="isAdditionalQuestionOpened" type="button" class="war-room-sign-up-modal__back">
              <img
                src="./back-arrow.svg"
                width="24"
                height="24"
                alt="Back"
                @click="isAdditionalQuestionOpened = false"
              />
            </button>
          </FadeTransition>
        </template>

        <section
          class="war-room-sign-up-modal__main-screen"
          :class="{ '--aditional-question-opened': isAdditionalQuestionOpened }"
          :inert="isAdditionalQuestionOpened"
          :aria-hidden="isAdditionalQuestionOpened"
        >
          <div v-if="!isUserInsideExternalApp">
            <template v-if="type === 'connection'">
              <BravadoText tag="p" variant="headline-semibold" class="war-room-sign-up-modal__vip-title">
                VIP invite for {{ $route.query.signup_invitee_name || 'you' }}
              </BravadoText>
              <BravadoText
                v-if="$route.query.signup_title"
                tag="h2"
                variant="header2"
                class="war-room-sign-up-modal__title"
              >
                {{ $route.query.signup_title }}
              </BravadoText>
              <BravadoText v-else tag="h2" variant="header2" class="war-room-sign-up-modal__title">
                Join {{ NUMBER_OF_USERS }}+ sales professionals
              </BravadoText>
              <BravadoText
                v-if="$route.query.signup_description"
                tag="p"
                variant="subhead"
                class="war-room-sign-up-modal__description"
              >
                {{ $route.query.signup_description }}
              </BravadoText>
              <BravadoText v-else tag="p" variant="subhead" class="war-room-sign-up-modal__description">
                {{ escapedCompanyName || 'Your company' }} was voted one of the top 50 sales teams in the United States,
                which is how you were chosen to receive this invite.
              </BravadoText>
            </template>
            <template v-if="type === 'university'">
              <BravadoText tag="p" variant="headline-semibold" class="war-room-sign-up-modal__vip-title">
                VIP invite for {{ $route.query.signup_invitee_name || 'you' }}
              </BravadoText>
              <BravadoText
                v-if="$route.query.signup_title"
                tag="h2"
                variant="header2"
                class="war-room-sign-up-modal__title"
              >
                {{ $route.query.signup_title }}
              </BravadoText>
              <BravadoText v-else tag="h2" variant="header2" class="war-room-sign-up-modal__title">
                Join 2000+
                <br />
                {{ escapedUniversityName || 'University' }} alumni
              </BravadoText>
              <BravadoText
                v-if="$route.query.signup_description"
                tag="p"
                variant="subhead"
                class="war-room-sign-up-modal__description"
              >
                {{ $route.query.signup_description }}
              </BravadoText>
              <BravadoText v-else tag="p" variant="subhead" class="war-room-sign-up-modal__description">
                Someone from {{ escapedUniversityName || 'your University' }} thought you would be a great fit for our
                sales community
              </BravadoText>
            </template>
            <template v-if="type === 'salesgpt'">
              <img src="./sales-gpt-logo.svg" alt="" width="40" height="48" class="war-room-sign-up-modal__logo" />
              <BravadoText tag="h2" variant="header3" class="war-room-sign-up-modal__title">
                Insights are
                <br />
                one step away
              </BravadoText>
              <BravadoText tag="p" variant="body-semibold" class="war-room-sign-up-modal__hint">
                Sign into SalesGPT to view your answer
              </BravadoText>
            </template>
            <BravadoText v-if="type === 'default'" tag="h2" variant="header5" class="war-room-sign-up-modal__title">
              Join our community of over
              <BravadoText tag="strong" variant="hero">{{ NUMBER_OF_USERS }}</BravadoText>
              top sales professionals from around the globe.
            </BravadoText>
            <SignUpSocialButtons
              class="war-room-sign-up-modal__social-buttons"
              :login-params="loginParams"
              :is-handled-outside="withAdditionalQuestion"
              @login="onSocialLogin"
            />
          </div>

          <div v-else>
            <BravadoText tag="h2" variant="header5">Join us today!</BravadoText>
            <BravadoText tag="p" variant="body" class="war-room-sign-up-modal__title-webview">
              Join our community of over {{ NUMBER_OF_USERS }} top sales professionals from around the globe.
            </BravadoText>
            <SignUpForm
              class="war-room-sign-up-modal__form"
              :promo="promo"
              :referer="referer"
              auth-source="sign-up-modal"
            />
          </div>

          <BravadoText tag="p" variant="caption1" class="war-room-sign-up-modal__membership">
            <strong>Membership is free.</strong>
            By signing up you agree with our
            <a href="https://info.bravado.co/bravado-network-terms-of-service-for-users" target="_blank">
              terms and conditions
            </a>
          </BravadoText>

          <BravadoText class="war-room-sign-up-modal__login" tag="p" variant="body">
            Already a member?
            <button data-test="open-log-in-block" class="war-room-sign-up-modal__cta" @click="navigateToSignIn">
              Log In
            </button>
          </BravadoText>

          <BravadoText
            v-if="type !== 'university' && type !== 'connection'"
            class="war-room-sign-up-modal__note"
            tag="small"
            variant="caption1"
          >
            *We require Linkedin authentication to verify that you are a sales professional
          </BravadoText>
        </section>

        <section
          v-if="withAdditionalQuestion"
          ref="additionalQuestion"
          class="war-room-sign-up-modal__additional-question"
          :class="{ '--aditional-question-opened': isAdditionalQuestionOpened }"
          :inert="!isAdditionalQuestionOpened"
          :aria-hidden="!isAdditionalQuestionOpened"
        >
          <BravadoText tag="h2" variant="header5" class="war-room-sign-up-modal__title">
            Help us personalize your Bravado experience
          </BravadoText>
          <BravadoText tag="label" variant="body-semibold" class="war-room-sign-up-modal__label">
            Your job title
          </BravadoText>
          <CurrentTitleAutocomplete
            class="war-room-sign-up-modal__job-title"
            placeholder="Account Executive"
            :value="jobTitle"
            :with-add-new="false"
            @input="handleJobTitleChanged"
          />
          <BravadoButtonV3
            class="war-room-sign-up-modal__submit"
            type="button"
            variant="primary"
            :disabled="jobTitle.length <= 2"
            full-width
            data-test="submit-title"
            @click="handleAdditionalAnswerReplied"
          >
            Continue
          </BravadoButtonV3>
        </section>
      </ModalBlack>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, useStore, ref, useContext, useRoute, onMounted, nextTick, watch } from '@nuxtjs/composition-api';
import SignUpSocialButtons from './SignUpSocialButtons/SignUpSocialButtons.vue';
import SignUpForm from './SignUpForm/SignUpForm.vue';
import { NUMBER_OF_USERS } from './constants';
import ModalBlack from './ModalBlack/ModalBlack.vue';
import { SIGN_UP_MODAL } from '@/constants/dynamicModals';
import type { RootState } from '~/types/store';
import CurrentTitleAutocomplete from '@/components/common/CurrentTitleAutocomplete/CurrentTitleAutocomplete.vue';
import Modal from '@/components/common/Modal/Modal.vue';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { COOKIE_NAME_SAVED_JOB_TITLE } from '@/components/common/constants';
import FadeTransition from '@/components/common/Transitions/FadeTransition.vue';
import {
  isUserInsideExternalApp as isUserInsideExternalAppUtil,
  isSalesGptReferrer,
} from '~/utils/externalRedirections';
import { escapeIterableQueryParam } from '~/components/utils/core/url';

const { $pages, $device, $analytics, $modal, $cookies } = useContext();
const route = useRoute();
const store = useStore<RootState>();

const isUserInsideExternalApp = ref(false);
const jobTitle = ref('');
const isAdditionalQuestionOpened = ref(false);
const linkToOAuthPage = ref<string | undefined>(undefined);
const additionalQuestion = ref<HTMLElement | null>(null);

const promo = computed(() => store.state.promo);
const referer = computed(() => (route.query?.refferer || route.query?.referer) as string);
const isSalesGptReferer = computed(
  () => isSalesGptReferrer(process.client ? document.referrer : '') || referer.value?.includes('search'),
);

/**
 * We show additional question about job title only on /login-external before sign up
 */
const withAdditionalQuestion = computed(() => {
  return (
    [$pages.LOGIN_EXTERNAL_PAGE.name].includes(route.name ?? '') &&
    !isUserInsideExternalApp.value &&
    isSalesGptReferer.value
  );
});

/**
 * Modal has different appearance for: users from salesgpt landing,
 * from connections and universities mails.
 * Used params:
 * - invite_type
 * - signup_company_name
 * - signup_university_name
 * - signup_invitee_name
 * - signup_description - if we want to replace text fully
 * - signup_title - if we want to replace title fully
 */
const type = computed(() => {
  if (isSalesGptReferer.value) return 'salesgpt';
  if (route.query.invite_type === 'connection') return 'connection';
  if (route.query.invite_type === 'university') return 'university';
  return 'default';
});

const loginParams = computed(() => ({
  ...(promo.value && { promo: promo.value }),
  auth_source: 'sign-up-modal',
}));

const escapedUniversityName = computed(() => escapeIterableQueryParam(route.query.signup_university_name as string));
const escapedCompanyName = computed(() => escapeIterableQueryParam(route.query.signup_company_name as string));

function setIsUserInsideExternalApp() {
  if (process.server) return;
  isUserInsideExternalApp.value = isUserInsideExternalAppUtil(
    document.referrer,
    navigator.userAgent,
    $device.isWebView,
  );
}

onMounted(() => {
  if (route.query.dynamic_modal !== 'sign-up') {
    $modal.hide(SIGN_UP_MODAL);
    return;
  }

  setIsUserInsideExternalApp();
});

watch(
  () => route.query.dynamic_modal,
  () => {
    setIsUserInsideExternalApp();
  },
);

function onSocialLogin(provider: 'google' | 'linkedin', link?: string) {
  if (withAdditionalQuestion.value) {
    linkToOAuthPage.value = link;
    isAdditionalQuestionOpened.value = true;
  }

  $analytics?.logEvent(`global__war-room-sign-up-modal__${provider}-button__click`);
}

function navigateToSignIn() {
  $analytics?.logEvent('global__war-room-sign-up-modal__login-link__click');
  $modal.showWithRoute('SIGN_IN_MODAL');
}

function handleJobTitleChanged(title: string) {
  jobTitle.value = title;
}

function handleAdditionalAnswerReplied() {
  $analytics?.logEvent('global__war-room-sign-up-modal__additional-answer__submit');
  $cookies?.set(COOKIE_NAME_SAVED_JOB_TITLE, jobTitle.value, { maxAge: 60 * 60 * 24, path: '/' }); // 1 day
  nextTick(() => {
    if (linkToOAuthPage.value) window.location.assign(linkToOAuthPage.value);
  });
}

watch(isAdditionalQuestionOpened, newVal => {
  if (newVal) {
    // Set focus after the animation to prevent the keyboard from popping up
    setTimeout(() => {
      additionalQuestion.value?.querySelector('input')?.focus();
    }, 500);
  }
});
</script>

<style lang="scss">
.war-room-sign-up-modal {
  --bounce-out: cubic-bezier(0.37, 1.4, 0.42, 0.95);
  // for animation between screens without changing height

  height: 530px !important;
  overflow-x: hidden;

  &_extended-height {
    height: 650px !important;
  }

  &__main-screen {
    position: absolute;
    transition: transform 0.3s var(--bounce-out), opacity 0.3s ease-in-out;

    &.--aditional-question-opened {
      transform: translateX(-500px);
      opacity: 0;
    }
  }

  &__additional-question {
    position: absolute;
    padding-top: 100px;
    transform: translateX(500px);
    opacity: 0;
    transition: transform 0.3s var(--bounce-out), opacity 0.3s ease-in-out;

    &.--aditional-question-opened {
      transform: translateX(0);
      opacity: 1;
    }
  }

  &__social-buttons {
    margin-top: 24px;
    margin-bottom: 12px;
  }

  &__title {
    strong {
      display: block;
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  &__logo {
    margin: 0 0 8px;
  }

  &__hint {
    margin-top: 16px;
    color: var(--gray-50);
  }

  &__title-webview {
    margin-top: 16px;
    margin-bottom: 20px;
  }

  &__membership {
    margin-bottom: 18px;

    strong {
      font-weight: 600;
    }

    a {
      text-decoration: underline;
    }
  }

  &__section {
    margin: 16px auto;
  }

  &__login {
    color: var(--gray-50);
    margin-bottom: 18px;
  }

  &__cta {
    display: inline;
    font: inherit;
    border: none;
    padding: 0;
    margin: 0;
    background: transparent;
    cursor: pointer;
    color: rgba(var(--white));
    font-weight: 600;
  }

  &__note {
    display: block;
    color: var(--gray-50);
  }

  &__form {
    margin-top: 22px;
  }

  &__job-title {
    margin-bottom: 18px;
    text-align: left;
  }

  &__label {
    text-align: left;
    display: block;
    margin-top: 18px;
    margin-bottom: 4px;
  }

  &__back {
    @include unstyled-button;

    position: absolute;
    z-index: 2;
    top: 16px;
    left: 16px;
    cursor: pointer;
  }

  &__description {
    margin-top: 16px;
    color: rgba(var(--white));
  }

  &__vip-title {
    color: var(--gold-20);
    text-transform: uppercase;
    letter-spacing: 2px;
    margin-bottom: 20px;
  }
}
</style>
